import { useEffect, useRef, useState } from 'react';
import { ArrowRightLeft, Pill, Plus, RotateCw, X, type LucideIcon } from 'lucide-react';
import { RefillApiError, refillApi, type RefillKind, type RefillPageInfo } from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { hasErrors, type FieldErrors } from '../../lib/validate';
import { Alert, Button, Field, FOCUS_RING, IconTile, Input, StepActions, cn } from '../../components/ui';
import { TextRemindersBox } from '../booking/shared';
import {
  EMPTY_REQUEST,
  MAX_EMAIL,
  MAX_ITEMS,
  MAX_NOTE,
  MAX_TEXT,
  buildSubmit,
  shownPhone,
  todayIso,
  validateRequest,
  type RequestDraft,
  type RequestField,
} from './refill-request';
import { StepHeading } from './StepHeading';

const KIND_WORDS: Record<RefillKind, { title: MessageKey; help: MessageKey; icon: LucideIcon }> = {
  refill: { title: 'refill.kind.refill', help: 'refill.kind.refillHelp', icon: Pill },
  renewal: { title: 'refill.kind.renewal', help: 'refill.kind.renewalHelp', icon: RotateCw },
  transfer: { title: 'refill.kind.transfer', help: 'refill.kind.transferHelp', icon: ArrowRightLeft },
};

/** What the list of prescriptions is called, and each box in it, for each kind of request. */
const ITEM_WORDS: Record<RefillKind, { legend: MessageKey; help: MessageKey; each: MessageKey }> = {
  refill: { legend: 'refill.items.refill', help: 'refill.items.refillHelp', each: 'refill.item.refill' },
  renewal: { legend: 'refill.items.renewal', help: 'refill.items.renewalHelp', each: 'refill.item.renewal' },
  transfer: { legend: 'refill.items.transfer', help: 'refill.items.transferHelp', each: 'refill.item.transfer' },
};

/** Where each problem in the summary takes the patient. */
const FIELD_IDS: Record<RequestField, string> = {
  kind: 'refill-kind-refill',
  items: 'refill-item-0',
  fromName: 'refill-from-name',
  fromPhone: 'refill-from-phone',
  neededBy: 'refill-needed-by',
  note: 'refill-note',
  email: 'refill-email',
};

/**
 * What the patient had filled in on this step, kept by the page while they go back to change their
 * number (ORX-D19), so they do not type their medications again. In the page's memory only.
 */
export type KeptRequest = { draft: RequestDraft; texts: boolean };

const TEXTAREA = cn(
  'min-h-24 w-full rounded-lg border border-input bg-card px-3.5 py-2.5 text-base text-foreground shadow-sm',
  'hover:border-foreground/50 focus-visible:border-primary',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
  'aria-[invalid=true]:border-danger',
);

/**
 * Track ORX — step 3, the request itself: a refill (prescription numbers or medication names), a
 * renewal (the medication, and the prescriber if known) or a transfer (the pharmacy it's at now and
 * the medications); an optional date it's needed by and a short note; and "Text me when it's
 * ready", unticked until the patient ticks it (ORX-D8).
 *
 * The medications are health information: they go in the submit's body and nowhere else, never in
 * the address, the console or anything kept in the browser.
 *
 * Track ORX follow-ups (2026-10-29): an optional email for "we got your request" and "ready for
 * pickup" (ORX-D20), and, when no code proved the number, the number to check with a way back to
 * change it (ORX-D19).
 *
 * ORX-D24: when the start said the number once texted STOP, there is no "Text me when it's ready"
 * (a note above the email box says why and asks for an email instead), and the submit always sends
 * `textWhenReady: false`.
 */
export function RequestStep({
  info,
  submitToken,
  phoneToCheck,
  textsStopped = false,
  kept,
  onChangeNumber,
  onDone,
  onExpired,
  onUnavailable,
}: {
  info: RefillPageInfo;
  submitToken: string;
  /**
   * ORX-D19 — the number as typed, when no texted code proved it: the step asks the patient to
   * check it. Absent after a code, which proved it.
   */
  phoneToCheck?: string;
  /** ORX-D24 — the number once texted STOP: no "ready" text can reach it. */
  textsStopped?: boolean;
  /** What this step held before the patient went back to change their number. */
  kept?: KeptRequest;
  /** "Change number": back to who they are, with what is typed here kept. */
  onChangeNumber?: (kept: KeptRequest) => void;
  onDone: (result: { reference: string; textWhenReady: boolean; email: string | null }) => void;
  /** The submit token ran out (30 minutes) or was spent: the patient starts again. */
  onExpired: () => void;
  onUnavailable: () => void;
}) {
  const { t } = useI18n();
  const kinds = info.types.length > 0 ? info.types : (['refill', 'renewal', 'transfer'] as RefillKind[]);
  const [draft, setDraft] = useState<RequestDraft>(
    () =>
      kept?.draft ?? {
        ...EMPTY_REQUEST,
        // One kind offered is a kind already chosen.
        kind: kinds.length === 1 ? kinds[0]! : null,
      },
  );
  const [texts, setTexts] = useState(kept?.texts ?? false);
  const [errors, setErrors] = useState<FieldErrors<RequestField>>({});
  const [refusal, setRefusal] = useState<MessageKey | null>(null);
  const [busy, setBusy] = useState(false);
  const summary = useRef<HTMLDivElement>(null);
  const itemBoxes = useRef<(HTMLInputElement | null)[]>([]);
  const [focusItem, setFocusItem] = useState<number | null>(null);
  const today = todayIso();

  useEffect(() => {
    if (focusItem === null) return;
    itemBoxes.current[focusItem]?.focus();
    setFocusItem(null);
  }, [focusItem]);

  const set = (changes: Partial<RequestDraft>): void => setDraft((current) => ({ ...current, ...changes }));
  const setItem = (index: number, value: string): void =>
    setDraft((current) => ({ ...current, items: current.items.map((item, i) => (i === index ? value : item)) }));
  const addItem = (): void => {
    if (draft.items.length >= MAX_ITEMS) return;
    set({ items: [...draft.items, ''] });
    setFocusItem(draft.items.length);
  };
  const removeItem = (index: number): void => {
    set({ items: draft.items.filter((_, i) => i !== index) });
    setFocusItem(Math.max(0, index - 1));
  };

  const send = async (): Promise<void> => {
    const found = validateRequest(draft, today);
    setErrors(found);
    setRefusal(null);
    if (hasErrors(found) || !draft.kind) {
      setTimeout(() => summary.current?.focus(), 0);
      return;
    }
    setBusy(true);
    // ORX-D24 — never a text to a number that texted STOP, whatever the (hidden) box was left at.
    const textWhenReady = texts && !textsStopped;
    try {
      const { reference } = await refillApi.submit(
        buildSubmit({ ...draft, kind: draft.kind }, submitToken, textWhenReady),
      );
      onDone({ reference, textWhenReady, email: draft.email.trim() || null });
    } catch (error) {
      const code = error instanceof RefillApiError ? error.code : 'unknown';
      if (code === 'expired' || code === 'locked') return onExpired();
      if (code === 'not_available') return onUnavailable();
      if (code === 'email_invalid') {
        // ORX-D20 — said on the box; the server kept the token, so a corrected address goes as is.
        setErrors({ email: 'refill.emailInvalid' });
        setTimeout(() => summary.current?.focus(), 0);
        return;
      }
      setRefusal(
        code === 'invalid'
          ? 'refill.checkRequest'
          : code === 'too_many'
            ? 'refill.tooMany'
            : code === 'offline'
              ? 'error.offline'
              : 'error.unknown',
      );
      setTimeout(() => summary.current?.focus(), 0);
    } finally {
      setBusy(false);
    }
  };

  const fieldErrors = Object.entries(errors) as [RequestField, MessageKey][];
  const kind = draft.kind;
  const words = kind ? ITEM_WORDS[kind] : null;

  return (
    <form
      noValidate
      className="space-y-7"
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <StepHeading>{t('refill.requestHeading')}</StepHeading>

      {phoneToCheck !== undefined && onChangeNumber && (
        // ORX-D19 — without a code, a typo would send "ready" texts to a stranger.
        <div className="flex flex-wrap items-center gap-x-2 rounded-xl border border-border bg-muted/70 px-4 py-2">
          <p className="text-base text-foreground">{t('refill.phoneCheck', { phone: shownPhone(phoneToCheck) })}</p>
          <Button variant="link" onClick={() => onChangeNumber({ draft, texts })}>
            {t('refill.changeNumber')}
          </Button>
        </div>
      )}

      {(fieldErrors.length > 0 || refusal) && (
        <div ref={summary} tabIndex={-1} className="focus:outline-none">
          <Alert>
            {refusal ? (
              t(refusal)
            ) : (
              <>
                {t('error.summary')}
                <ul className="mt-1 list-disc pl-5">
                  {fieldErrors.map(([name, key]) => (
                    <li key={name}>
                      <a
                        href={`#${FIELD_IDS[name]}`}
                        onClick={(event) => {
                          event.preventDefault();
                          document.getElementById(FIELD_IDS[name])?.focus();
                        }}
                        className="text-danger underline underline-offset-4"
                      >
                        {t(key)}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Alert>
        </div>
      )}

      <fieldset aria-describedby={errors.kind ? 'refill-kind-error' : undefined}>
        <legend className="text-lg font-semibold text-heading">{t('refill.kindLegend')}</legend>
        <div className="mt-3 grid gap-3">
          {kinds.map((option) => {
            const copy = KIND_WORDS[option];
            const chosen = kind === option;
            return (
              <label
                key={option}
                htmlFor={`refill-kind-${option}`}
                className={cn(
                  'flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors',
                  chosen ? 'border-primary bg-accent/60' : 'border-input hover:border-primary/60',
                )}
              >
                <input
                  id={`refill-kind-${option}`}
                  type="radio"
                  name="refill-kind"
                  value={option}
                  checked={chosen}
                  onChange={() => set({ kind: option })}
                  className={cn('mt-1 h-5 w-5 shrink-0 accent-[hsl(var(--primary))]', FOCUS_RING)}
                />
                <IconTile icon={copy.icon} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block text-base font-semibold text-heading">{t(copy.title)}</span>
                  <span className="block text-sm text-muted-foreground">{t(copy.help)}</span>
                </span>
              </label>
            );
          })}
        </div>
        {errors.kind && (
          <p id="refill-kind-error" className="mt-2 text-sm font-medium text-danger">
            {t(errors.kind)}
          </p>
        )}
      </fieldset>

      {kind && words && (
        <>
          {kind === 'transfer' && (
            <section className="space-y-5">
              <Field
                id="refill-from-name"
                label={t('refill.fromName')}
                required
                requiredLabel={t('details.required')}
                error={errors.fromName && t(errors.fromName)}
              >
                {(props) => (
                  <Input
                    {...props}
                    maxLength={MAX_TEXT}
                    autoComplete="off"
                    value={draft.fromName}
                    onChange={(event) => set({ fromName: event.target.value })}
                  />
                )}
              </Field>
              <Field
                id="refill-from-phone"
                label={t('refill.fromPhone')}
                error={errors.fromPhone && t(errors.fromPhone)}
              >
                {(props) => (
                  <Input
                    {...props}
                    type="tel"
                    inputMode="tel"
                    autoComplete="off"
                    value={draft.fromPhone}
                    onChange={(event) => set({ fromPhone: event.target.value })}
                  />
                )}
              </Field>
            </section>
          )}

          <fieldset
            className="space-y-3"
            aria-describedby={cn('refill-items-help', errors.items && 'refill-items-error')}
          >
            <legend className="text-lg font-semibold text-heading">{t(words.legend)}</legend>
            <p id="refill-items-help" className="text-sm text-muted-foreground">
              {t(words.help)}
            </p>
            {draft.items.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <label htmlFor={`refill-item-${index}`} className="sr-only">
                  {t(words.each, { n: index + 1 })}
                </label>
                <Input
                  ref={(node) => {
                    itemBoxes.current[index] = node;
                  }}
                  id={`refill-item-${index}`}
                  maxLength={MAX_TEXT}
                  autoComplete="off"
                  aria-invalid={Boolean(errors.items) && index === 0}
                  value={item}
                  onChange={(event) => setItem(index, event.target.value)}
                />
                {draft.items.length > 1 && (
                  <Button
                    variant="ghost"
                    className="min-h-12 px-3"
                    aria-label={t('refill.itemRemove', { n: index + 1 })}
                    onClick={() => removeItem(index)}
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </Button>
                )}
              </div>
            ))}
            {draft.items.length < MAX_ITEMS ? (
              <Button variant="outline" size="sm" onClick={addItem}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                {t('refill.itemAdd')}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">{t('refill.itemsMax')}</p>
            )}
            {errors.items && (
              <p id="refill-items-error" className="text-sm font-medium text-danger">
                {t(errors.items)}
              </p>
            )}
          </fieldset>

          {kind === 'renewal' && (
            <Field id="refill-prescriber" label={t('refill.prescriber')}>
              {(props) => (
                <Input
                  {...props}
                  maxLength={MAX_TEXT}
                  autoComplete="off"
                  value={draft.prescriber}
                  onChange={(event) => set({ prescriber: event.target.value })}
                />
              )}
            </Field>
          )}

          <section className="space-y-5 border-t border-border pt-6">
            <div className="sm:w-1/2">
              <Field
                id="refill-needed-by"
                label={t('refill.neededBy')}
                help={t('refill.optional')}
                error={errors.neededBy && t(errors.neededBy)}
              >
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    min={today}
                    value={draft.neededBy}
                    onChange={(event) => set({ neededBy: event.target.value })}
                  />
                )}
              </Field>
            </div>
            <Field
              id="refill-note"
              label={t('refill.note')}
              help={t('refill.noteHelp')}
              error={errors.note && t(errors.note)}
            >
              {(props) => (
                <>
                  <textarea
                    {...props}
                    aria-describedby={cn(props['aria-describedby'], 'refill-note-count')}
                    rows={3}
                    maxLength={MAX_NOTE}
                    value={draft.note}
                    onChange={(event) => set({ note: event.target.value })}
                    className={TEXTAREA}
                  />
                  <p id="refill-note-count" className="text-right text-sm text-muted-foreground">
                    {t('refill.noteCount', { count: draft.note.length, max: MAX_NOTE })}
                  </p>
                </>
              )}
            </Field>

            {textsStopped && (
              // ORX-D24 — in the box's place, and above the email box it points to ("below").
              <Alert tone="info">{t('refill.textsStoppedNote', { pharmacy: info.pharmacyName })}</Alert>
            )}

            <div className="sm:w-2/3">
              <Field
                id="refill-email"
                label={t('refill.email')}
                help={t('refill.emailHelp')}
                error={errors.email && t(errors.email)}
              >
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    maxLength={MAX_EMAIL}
                    value={draft.email}
                    onChange={(event) => set({ email: event.target.value })}
                  />
                )}
              </Field>
            </div>

            {!textsStopped && (
              <TextRemindersBox
                wordings={info.textWhenReady?.wordings ?? []}
                checked={texts}
                onChange={setTexts}
                label={t('refill.textWhenReady')}
              />
            )}
          </section>
        </>
      )}

      <StepActions>
        <Button type="submit" loading={busy} className="flex-1 sm:flex-none">
          {t('refill.send')}
        </Button>
      </StepActions>
    </form>
  );
}
